import { useMutation, useQuery } from '@tanstack/react-query';
import { getAuthHeaders } from './client';

export interface ExportPdfParams {
  artifact_id: string;
  stylesheet_id?: string;
  options?: {
    page_size?: string;
    orientation?: string;
    margin_top?: string;
    margin_right?: string;
    margin_bottom?: string;
    margin_left?: string;
  };
}

export interface PdfGenerationJob {
  job_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  artifact_id: string;
  error?: string;
  result?: {
    pdf_storage_path: string;
    pdf_bytes_size: number;
  };
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

/**
 * Start async PDF generation
 *
 * Returns immediately with job_id, actual generation happens in background.
 * Poll with useGenerationStatus() to track progress.
 *
 * Workflow:
 * 1. User clicks "Save PDF"
 * 2. POST /api/pdf/generate-async returns { job_id, status: 'queued' }
 * 3. Frontend polls GET /api/pdf/generate/:jobId every 500ms
 * 4. Status: queued → processing → completed/failed
 * 5. On completion, PDF is available in Supabase Storage
 */
export const useStartPdfGeneration = () => {
  return useMutation({
    mutationFn: async (params: ExportPdfParams) => {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/pdf/generate-async', {
        method: 'POST',
        headers,
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to start PDF generation';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // If response isn't JSON, use generic message
        }
        throw new Error(errorMessage);
      }

      return response.json() as Promise<PdfGenerationJob>;
    },
  });
};

/**
 * Poll for PDF generation status
 *
 * Used with useStartPdfGeneration() to track progress.
 * Frontend automatically polls every 500ms while status is queued or processing.
 *
 * Example:
 * const startGen = useStartPdfGeneration();
 * const [jobId, setJobId] = useState<string | null>(null);
 *
 * const genStatus = useGenerationStatus(jobId);
 *
 * const handleSavePdf = async () => {
 *   const job = await startGen.mutateAsync({ artifact_id });
 *   setJobId(job.job_id);
 * };
 */
export const useGenerationStatus = (jobId: string | null) => {
  return useQuery({
    queryKey: ['pdf-generation', jobId],
    queryFn: async () => {
      if (!jobId) throw new Error('No job ID provided');

      const headers = await getAuthHeaders();
      const response = await fetch(`/api/pdf/generate/${jobId}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch generation status');
      const data = await response.json();
      return data as PdfGenerationJob;
    },
    enabled: !!jobId,
    // Poll every 500ms while generation is active
    // Use dynamic refetch interval based on status
    refetchInterval: (query) => {
      const data = query.state.data as PdfGenerationJob | null;
      if (!data) return false;
      // Poll while queued or processing, stop polling once complete/failed
      if (data.status === 'queued' || data.status === 'processing') {
        return 500; // Poll every 500ms
      }
      return false; // Stop polling
    },
    // Retry configuration for network failures
    retry: (failureCount) => {
      // Retry up to 3 times on network failures
      return failureCount < 3;
    },
    // Don't stale immediately - keep fresh data
    staleTime: 0,
  });
};

/**
 * Download a generated PDF from Supabase Storage
 *
 * Called once generation is complete to download the PDF
 */
export const downloadGeneratedPdf = async (
  pdfStoragePath: string,
  filename: string = 'resume.pdf'
): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    // Fetch PDF from Supabase Storage via backend
    const response = await fetch(`/api/pdf/download`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ storage_path: pdfStoragePath }),
    });

    if (!response.ok) {
      throw new Error('Failed to download PDF');
    }

    const pdfBlob = await response.blob();

    // Auto-download
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download PDF:', error);
    throw error;
  }
};

/**
 * Legacy: Export artifact as PDF (synchronous, blocking)
 *
 * Use useStartPdfGeneration() + useGenerationStatus() instead for better UX.
 * This endpoint is kept for backward compatibility and cache hits.
 *
 * Workflow:
 * - First request: Generates PDF via Document Converter Service, caches in Supabase Storage
 * - Subsequent requests: Downloads from cache (50-100ms instead of 2-3s)
 * - Auto-downloads PDF on success via onSuccess callback
 * - Returns PDF as Blob for browser download
 */
export const useExportPdf = () => {
  return useMutation({
    mutationFn: async (params: ExportPdfParams) => {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/pdf', {
        method: 'POST',
        headers,
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        let errorMessage = 'PDF export failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // If response isn't JSON, use generic message
        }
        throw new Error(errorMessage);
      }

      // Return as Blob for download
      return response.blob();
    },
    onSuccess: (pdfBlob, variables) => {
      // Auto-download PDF
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;

      // Use the artifact name if available, fallback to default
      const filename = variables.artifact_id ? `resume.pdf` : 'resume.pdf';
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

/**
 * Check if PDF export service is available
 *
 * Used to verify Document Converter Service health before showing export UI
 */
export const usePdfServiceHealth = () => {
  return useQuery({
    queryKey: ['pdf-health'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/pdf/health', { headers });
      if (!response.ok) throw new Error('PDF service unavailable');
      return response.json();
    },
    staleTime: 60000, // Cache for 1 minute
    retry: 1, // Only retry once
  });
};
